import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './RoutingGuide.scss';

const CreateRouteForm = ({ setSuccessMessage }) => {
  const [searchParams] = useSearchParams();
  const providerType = searchParams.get('providerType');
  const navigate = useNavigate();

  const [createRoute, setCreateRoute] = useState({
    origin: '',
    destination: '',
    provider: '',
    lcl_booking_closing_cutoff_offset_days: -1,
    lcl_cargo_closing_cutoff_offset_days: 0,
    lcl_documentation_closing_cutoff_offset_days: -1
  });

  const [originPortId, setOriginPortId] = useState('');
  const [destinationPortId, setDestinationPortId] = useState('');
  const [originPortCode, setOriginPortCode] = useState('');
  const [destinationPortCode, setDestinationPortCode] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState('');
  const [portPairExists, setPortPairExists] = useState(false);
  const [portPairError, setPortPairError] = useState('');
  const [isSaving, setIsSaving] = useState(false); // Added loading state
  const [originError, setOriginError] = useState(''); // Added originError state
  const [destinationError, setDestinationError] = useState(''); // Added destinationError state

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get('/api/providers', {
          params: { providerType },
        });
        setProviders(response.data);
      } catch (error) {
        console.error('Error fetching providers:', error);
      }
    };

    fetchProviders();
  }, [providerType]);

  const fetchPortSuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/port-names-by-uncode-or-name', { 
        params: { query: value },
        responseType: 'json'
      });
      if (response.headers['content-type'].includes('application/json')) {
        setSuggestions(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching port suggestions:', error);
    }
  };

  const checkPortPair = async (newOrigin, newDestination, newProvider) => {
    if (!newOrigin || !newDestination || !newProvider) {
      setPortPairExists(false);
      setPortPairError('');
      return;
    }
  
    try {
      const response = await axios.get(`/api/routes/check-exists`, {
        params: {
          origin: newOrigin,
          destination: newDestination,
          provider: newProvider
        }
      });
      if (response.data.exists) {
        setPortPairExists(true);
        setPortPairError('The port pair already exists for this LCL Schedule Provider.');
      } else {
        setPortPairExists(false);
        setPortPairError('');
      }
    } catch (error) {
      console.error('Error checking port pair:', error);
    }
  };

  const validateForm = () => { // Updated validation function
    let isValid = true;

    if (!originPortCode.trim() || !destinationPortCode.trim() || !createRoute.provider) { // Modified condition
      setError('All fields are required.');
      isValid = false;
    } else {
      setError('');
    }

    // Validate origin UN Location Code
    if (originPortCode.length !== 5 || /\s/.test(originPortCode)) {
      setOriginError('Invalid UN Location Code. Value must be exactly 5 characters long and contain no spaces Example: USNYC');
      isValid = false;
    } else {
      setOriginError('');
    }

    // Validate destination UN Location Code
    if (destinationPortCode.length !== 5 || /\s/.test(destinationPortCode)) {
      setDestinationError('Invalid UN Location Code. Value must be exactly 5 characters long and contain no spaces. Example: USNYC');
      isValid = false;
    } else {
      setDestinationError('');
    }

    if (portPairExists) {
      setError('Cannot create route: port pair already exists.');
      isValid = false;
    }

    return isValid;
  };

  const handleSaveCreateRoute = async () => {
    if (!validateForm()) return; // Use updated validation
    
    const {
      provider,
      lcl_booking_closing_cutoff_offset_days,
      lcl_cargo_closing_cutoff_offset_days,
      lcl_documentation_closing_cutoff_offset_days
    } = createRoute;
  
    const data = {
      origin: originPortCode, // Use originPortCode instead of createRoute.origin
      destination: destinationPortCode, // Use destinationPortCode instead of createRoute.destination
      provider,
      lcl_booking_closing_cutoff_offset_days,
      lcl_cargo_closing_cutoff_offset_days,
      lcl_documentation_closing_cutoff_offset_days
    };
  
    setIsSaving(true); // Set saving state
    try {
      const response = await axios.post(`/api/routes`, data);
      if (response.status === 201) {
        setSuccessMessage('Route was successfully created.');
        setError('');
        setTimeout(() => setSuccessMessage(''), 10000);
        navigate(`/routing-guide/list?providerType=${providerType}`);
      } else {
        setError('Unexpected response from server.');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Error creating route.'); // Enhanced error message
      console.error('Error creating route:', error);
    } finally {
      setIsSaving(false); // Reset saving state
    }
  };

  const handleCancel = () => {
    navigate(`/routing-guide/list?providerType=${providerType}`);
  };

  const inputProps = (name, value, onChange) => ({
    placeholder: `Enter ${name} code`,
    value,
    onChange
  });

  return (
    <div>
      <Button variant="secondary" onClick={handleCancel} className="mb-3">
        Back
      </Button>
      <h1>Create Route</h1>
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      <div className="form-section">
        <Form.Group controlId="origin">
          <Form.Label>Origin</Form.Label>
          <div style={{ position: 'relative' }}>
            <Autosuggest
              suggestions={originSuggestions}
              onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setOriginSuggestions)}
              onSuggestionsClearRequested={() => setOriginSuggestions([])}
              getSuggestionValue={suggestion => suggestion.port_code}
              renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
              inputProps={{
                ...inputProps('origin', originPortCode, (e, { newValue }) => {
                  setOriginPortCode(newValue);
                  setCreateRoute({ ...createRoute, origin: newValue }); // Synchronize origin
                  checkPortPair(newValue, destinationPortCode, createRoute.provider);
                })
              }}
              onSuggestionSelected={(e, { suggestion }) => {
                setOriginPortCode(suggestion.port_code);
                setOriginPortId(suggestion.port_id);
              }}
              theme={{
                container: 'react-autosuggest__container',
                input: 'react-autosuggest__input',
                suggestionsContainer: 'react-autosuggest__suggestions-container',
                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                suggestionsList: 'react-autosuggest__suggestions-list',
                suggestion: 'react-autosuggest__suggestion',
                suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
              }}
            />
            {originPortCode && destinationPortCode && createRoute.provider && (
              portPairExists ? (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'red' }}>✗</span>
              ) : (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'green' }}>✓</span>
              )
            )}
          </div>
          {portPairError && <div style={{ color: 'red' }}>{portPairError}</div>}
          {originError && <div style={{ color: 'red' }}>{originError}</div>} {/* Display origin error */}
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="destination">
          <Form.Label>Destination</Form.Label>
          <div style={{ position: 'relative' }}>
            <Autosuggest
              suggestions={destinationSuggestions}
              onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setDestinationSuggestions)}
              onSuggestionsClearRequested={() => setDestinationSuggestions([])}
              getSuggestionValue={suggestion => suggestion.port_code}
              renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
              inputProps={{
                ...inputProps('destination', destinationPortCode, (e, { newValue }) => {
                  setDestinationPortCode(newValue);
                  setCreateRoute({ ...createRoute, destination: newValue }); // Synchronize destination
                  checkPortPair(originPortCode, newValue, createRoute.provider);
                })
              }}
              onSuggestionSelected={(e, { suggestion }) => {
                setDestinationPortCode(suggestion.port_code);
                setDestinationPortId(suggestion.port_id);
              }}
              theme={{
                container: 'react-autosuggest__container',
                input: 'react-autosuggest__input',
                suggestionsContainer: 'react-autosuggest__suggestions-container',
                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                suggestionsList: 'react-autosuggest__suggestions-list',
                suggestion: 'react-autosuggest__suggestion',
                suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
              }}
            />
            {originPortCode && destinationPortCode && createRoute.provider && (
              portPairExists ? (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'red' }}>✗</span>
              ) : (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'green' }}>✓</span>
              )
            )}
          </div>
          {portPairError && <div style={{ color: 'red' }}>{portPairError}</div>}
          {destinationError && <div style={{ color: 'red' }}>{destinationError}</div>} {/* Display destination error */}
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="provider">
          <Form.Label>Provider</Form.Label>
          <Form.Control
            as="select"
            name="provider"
            value={createRoute.provider}
            onChange={(e) => {
              const newProvider = e.target.value;
              setCreateRoute({ ...createRoute, provider: newProvider });
              if (originPortCode && destinationPortCode && newProvider) {
                checkPortPair(originPortCode, destinationPortCode, newProvider);
              }
            }}
          >
            <option value="">Select...</option>
            {providers.map((provider) => (
              <option key={provider.provider_id} value={provider.provider_id}>
                {provider.provider_name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="lcl_booking_closing_cutoff_offset_days">
          <Form.Label>Booking Closing Offset Days</Form.Label>
          <Form.Control
            type="number"
            value={createRoute.lcl_booking_closing_cutoff_offset_days || 0}
            onChange={(e) =>
              setCreateRoute({ ...createRoute, lcl_booking_closing_cutoff_offset_days: parseInt(e.target.value, 10) || 0 })
            }
          />
          <small>The number of days to offset from the LCL schedule provider's POL cutoff date for Booking</small>
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="lcl_cargo_closing_cutoff_offset_days">
          <Form.Label>Cargo Closing Offset Days</Form.Label>
          <Form.Control
            type="number"
            value={createRoute.lcl_cargo_closing_cutoff_offset_days || 0}
            onChange={(e) =>
              setCreateRoute({ ...createRoute, lcl_cargo_closing_cutoff_offset_days: parseInt(e.target.value, 10) || 0 })
            }
          />
          <small>The number of days to offset from the LCL schedule provider's POL cutoff date for Cargo</small>
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="lcl_documentation_closing_cutoff_offset_days">
          <Form.Label>Documentation Closing Offset Days</Form.Label>
          <Form.Control
            type="number"
            value={createRoute.lcl_documentation_closing_cutoff_offset_days || 0}
            onChange={(e) =>
              setCreateRoute({ ...createRoute, lcl_documentation_closing_cutoff_offset_days: parseInt(e.target.value, 10) || 0 })
            }
          />
          <small>The number of days to offset from the LCL schedule provider's POL cutoff date for Documentation</small>
        </Form.Group>
      </div>
      <Button variant="primary" onClick={handleSaveCreateRoute} disabled={isSaving}>
        {isSaving ? <Spinner as="span" animation="border" size="sm" /> : 'Save'}
      </Button>
      <Button variant="secondary" onClick={handleCancel}>
        Cancel
      </Button>
    </div>
  );
};

export default CreateRouteForm;

